import React, { useEffect, useState } from "react";
import {
  getTestGradings,
  testGradingCreate,
  testGradingDelete,
  testGradingStatusUpdate,
  testGradingUpdate,
} from "../../api/TestGrading";

import Swal from "sweetalert2";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import TestLayout from "../main/testsList/TestLayout";
import usePrivateLink from "../config/usePrivateLink";

export default function TestGrading() {
  const [loading, setLoading] = useState(false);
  const [gradings, setGradings] = useState([]);
  const [search, setSearch] = useState("");
  const hasAccess = usePrivateLink();
  const [addData, setAddData] = useState({
    grade: "",
    from_percentage: "",
    to_percentage: "",
    status: "1",
  });

  const [updateData, setUpdateData] = useState({
    id: "",
    grade: "",
    from_percentage: "",
    to_percentage: "",
    status: "1",
  });

  const [statusData, setStatusData] = useState({
    id: "",
    grade: "",
    status: "1",
  });

  // =========================
  // GET GRADING LIST
  // =========================
  const getGradingList = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (search) {
        addUrl += `?search=${encodeURIComponent(search)}`;
      }

      const response = await getTestGradings(addUrl);

      if (response?.data?.status) {
        setGradings(response?.data?.data || []);
      } else {
        setGradings([]);
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No grading found",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGradingList();
  }, []);

  // =========================
  // SEARCH
  // =========================
  const handleSearch = () => {
    getGradingList();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      getGradingList();
    }
  };

  // =========================
  // ADD GRADING
  // =========================
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!addData.grade) {
      SweetAlertShow("info", "Warning", "Please enter grade");
      return;
    }

    if (addData.from_percentage === "" || addData.to_percentage === "") {
      SweetAlertShow("info", "Warning", "Please enter percentage range");
      return;
    }

    if (Number(addData.from_percentage) > Number(addData.to_percentage)) {
      SweetAlertShow(
        "info",
        "Warning",
        "From percentage cannot be greater than To percentage",
      );
      return;
    }

    setLoading(true);

    try {
      const response = await testGradingCreate(addData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Success",
          response?.data?.message || "Grading created successfully",
        );

        setAddData({
          grade: "",
          from_percentage: "",
          to_percentage: "",
          status: "1",
        });

        document.getElementById("addGradingClose")?.click();

        getGradingList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // OPEN UPDATE MODAL
  // =========================
  const openUpdateModal = (item) => {
    setUpdateData({
      id: item?.id || "",
      grade: item?.grade || "",
      from_percentage: item?.from_percentage || "",
      to_percentage: item?.to_percentage || "",
      status: item?.status || "1",
    });
  };

  // =========================
  // UPDATE GRADING
  // =========================
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!updateData.grade) {
      SweetAlertShow("info", "Warning", "Please enter grade");
      return;
    }

    if (updateData.from_percentage === "" || updateData.to_percentage === "") {
      SweetAlertShow("info", "Warning", "Please enter percentage range");
      return;
    }

    if (Number(updateData.from_percentage) > Number(updateData.to_percentage)) {
      SweetAlertShow(
        "info",
        "Warning",
        "From percentage cannot be greater than To percentage",
      );
      return;
    }

    setLoading(true);

    try {
      const response = await testGradingUpdate(updateData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Success",
          response?.data?.message || "Grading updated successfully",
        );

        document.getElementById("updateGradingClose")?.click();

        getGradingList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // DELETE GRADING
  // =========================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Remove Grade?",
      text: "This grading configuration will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Remove It",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    try {
      const response = await testGradingDelete(id);

      if (response?.data?.status) {
        Swal.fire(
          "Removed!",
          response?.data?.message || "Grading removed successfully",
          "success",
        );

        getGradingList();
      } else {
        Swal.fire("Warning", response?.data?.message, "info");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // OPEN STATUS MODAL
  // =========================
  const openStatusModal = (item) => {
    setStatusData({
      id: item?.id,
      grade: item?.grade,
      status: item?.status || "1",
    });
  };

  // =========================
  // UPDATE STATUS
  // =========================
  const handleStatusUpdate = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await testGradingStatusUpdate({
        id: statusData.id,
        status: statusData.status,
      });

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Success",
          response?.data?.message || "Status updated successfully",
        );

        document.getElementById("statusGradingClose")?.click();

        getGradingList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row">
                  <TestLayout />

                  <div className="col-12">
                    {/* =====================================================
                                            PAGE HEADER
                                        ====================================================== */}
                    <div className="grading-page-header mb-3">
                      <div className="grading-header-left">
                        <div className="grading-header-icon">
                          <i className="ri-bar-chart-box-line"></i>
                        </div>

                        <div>
                          <h4 className="mb-1">Test Grading</h4>

                          <p className="mb-0">
                            Define grade ranges based on student percentage
                          </p>
                        </div>
                      </div>

                      {hasAccess("/test/grading/add") && (
                        <button
                          type="button"
                          className="btn btn-primary grading-add-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#addGradingModal"
                        >
                          <i className="ri-add-line me-1"></i>
                          Add Grade
                        </button>
                      )}
                    </div>

                    {/* =====================================================
                                            SEARCH CARD
                                        ====================================================== */}
                    <div className="card grading-filter-card mb-3">
                      <div className="card-body">
                        <div className="grading-filter-header">
                          <div className="d-flex align-items-center gap-2">
                            <div className="grading-filter-icon">
                              <i className="ri-filter-3-line"></i>
                            </div>

                            <div>
                              <h6 className="mb-0">Find Grade</h6>

                              <small className="text-muted">
                                Search existing grading configurations
                              </small>
                            </div>
                          </div>

                          <span className="badge bg-label-primary">
                            {gradings?.length || 0} Records
                          </span>
                        </div>

                        <div className="row g-2 mt-2">
                          <div className="col-lg-6 col-md-7">
                            <div className="grading-search-box">
                              <i className="ri-search-line"></i>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search by grade..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                              />

                              {search && (
                                <button
                                  type="button"
                                  className="grading-search-clear"
                                  onClick={() => {
                                    setSearch("");
                                    setTimeout(() => {
                                      getGradingList();
                                    }, 50);
                                  }}
                                  title="Clear search"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="col-lg-2 col-md-3">
                            <button
                              type="button"
                              className="btn btn-primary w-100 grading-filter-btn"
                              onClick={handleSearch}
                            >
                              <i className="ri-search-line me-1"></i>
                              Search
                            </button>
                          </div>

                          <div className="col-lg-2 col-md-2">
                            <button
                              type="button"
                              className="btn btn-label-secondary w-100 grading-filter-btn"
                              onClick={() => {
                                setSearch("");
                                setTimeout(() => {
                                  getGradingList();
                                }, 100);
                              }}
                            >
                              <i className="ri-refresh-line me-1"></i>
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                                            GRADING TABLE
                                        ====================================================== */}
                    <div className="card grading-list-card">
                      <div className="card-header grading-list-header">
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <h5 className="mb-0">Grade Configuration</h5>

                            <span className="badge bg-label-primary">
                              {gradings?.length || 0}
                            </span>
                          </div>

                          <small className="text-muted">
                            Percentage ranges used for test result grading
                          </small>
                        </div>
                      </div>

                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table grading-table align-middle mb-0">
                            <thead>
                              <tr>
                                <th className="text-center grading-number-col">
                                  #
                                </th>

                                <th>Grade</th>

                                <th>From</th>

                                <th>To</th>

                                <th>Status</th>

                                <th className="text-center grading-action-col">
                                  Actions
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {gradings?.length > 0 ? (
                                gradings.map((item, index) => (
                                  <tr key={item.id}>
                                    {/* Number */}
                                    <td className="text-center">
                                      <span className="grading-row-number">
                                        {index + 1}
                                      </span>
                                    </td>

                                    {/* Grade */}
                                    <td>
                                      <div className="grade-cell">
                                        <div>
                                          <div className="grade-name">
                                            {item?.grade || "-"}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* From */}
                                    <td>
                                      <span className="percentage-value">
                                        {item?.from_percentage}%
                                      </span>
                                    </td>

                                    {/* To */}
                                    <td>
                                      <span className="percentage-value">
                                        {item?.to_percentage}%
                                      </span>
                                    </td>

                                    {/* Status */}
                                    <td>
                                      <button
                                        type="button"
                                        data-bs-toggle="modal"
                                        data-bs-target="#statusGradingModal"
                                        onClick={() => openStatusModal(item)}
                                        className={`grading-status-btn ${item?.status === "1"
                                          ? "active"
                                          : "inactive"
                                          }`}
                                      >
                                        <span className="status-dot"></span>

                                        {item?.status === "1"
                                          ? "Active"
                                          : "Inactive"}

                                        <i className="ri-arrow-right-s-line"></i>
                                      </button>
                                    </td>

                                    {/* Actions */}
                                    <td>
                                      <div className="grading-actions">
                                        {hasAccess("/test/grading/update") && (
                                          <button
                                            type="button"
                                            className="grading-action-btn edit"
                                            data-bs-toggle="modal"
                                            data-bs-target="#updateGradingModal"
                                            onClick={() => openUpdateModal(item)}
                                            title="Edit Grade"
                                          >
                                            <i className="ri-pencil-line"></i>
                                          </button>
                                        )}
                                        {hasAccess("/test/grading/delete") && (
                                          <button
                                            type="button"
                                            onClick={() => handleDelete(item?.id)}
                                            className="grading-action-btn remove"
                                            title="Remove Grade"
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="border-0">
                                    <div className="grading-empty-state">
                                      <div className="grading-empty-icon">
                                        <i className="ri-bar-chart-grouped-line"></i>
                                      </div>

                                      <h6>No Grading Records</h6>

                                      <p>
                                        No grading configuration matches your
                                        search.
                                      </p>

                                      {search && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-label-primary"
                                          onClick={() => {
                                            setSearch("");
                                            setTimeout(() => {
                                              getGradingList();
                                            }, 50);
                                          }}
                                        >
                                          <i className="ri-refresh-line me-1"></i>
                                          Clear Search
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================
                ADD GRADING MODAL
            ================================================= */}
      <div
        className="modal fade"
        id="addGradingModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <form
            className="modal-content grading-modal"
            onSubmit={handleAddSubmit}
          >
            <div className="modal-header grading-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="grading-modal-icon primary">
                  <i className="ri-add-line"></i>
                </div>

                <div>
                  <h5 className="modal-title mb-1">Add New Grade</h5>

                  <small className="text-muted">
                    Create a percentage-based grade
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                id="addGradingClose"
              ></button>
            </div>

            <div className="modal-body">
              <div className="grading-form-section">
                <div className="grading-form-section-title">
                  <i className="ri-award-line"></i>
                  Grade Details
                </div>

                <div className="form-floating form-floating-outline">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Grade"
                    value={addData.grade}
                    onChange={(e) =>
                      setAddData({
                        ...addData,
                        grade: e.target.value.toUpperCase(),
                      })
                    }
                  />

                  <label>Grade</label>
                </div>
              </div>

              <div className="grading-form-section mt-4">
                <div className="grading-form-section-title">
                  <i className="ri-percent-line"></i>
                  Percentage Range
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="form-control"
                        placeholder="From Percentage"
                        value={addData.from_percentage}
                        onChange={(e) =>
                          setAddData({
                            ...addData,
                            from_percentage: e.target.value,
                          })
                        }
                      />

                      <label>From (%)</label>
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="form-control"
                        placeholder="To Percentage"
                        value={addData.to_percentage}
                        onChange={(e) =>
                          setAddData({
                            ...addData,
                            to_percentage: e.target.value,
                          })
                        }
                      />

                      <label>To (%)</label>
                    </div>
                  </div>
                </div>

                <div className="grading-example-box mt-3">
                  <div className="grading-example-icon">
                    <i className="ri-lightbulb-line"></i>
                  </div>

                  <div>
                    <strong>Example</strong>
                    <span>Grade A can be configured from 81% to 100%.</span>
                  </div>
                </div>
              </div>

              <div className="grading-form-section mt-4">
                <div className="grading-form-section-title">
                  <i className="ri-toggle-line"></i>
                  Availability
                </div>

                <div className="form-floating form-floating-outline">
                  <select
                    className="form-select"
                    value={addData.status}
                    onChange={(e) =>
                      setAddData({
                        ...addData,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                  </select>

                  <label>Status</label>
                </div>
              </div>
            </div>

            <div className="modal-footer grading-modal-footer">
              <button
                type="button"
                className="btn btn-label-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                <i className="ri-save-line me-1"></i>
                Save Grade
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* =================================================
                UPDATE GRADING MODAL
            ================================================= */}
      <div
        className="modal fade"
        id="updateGradingModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <form
            className="modal-content grading-modal"
            onSubmit={handleUpdateSubmit}
          >
            <div className="modal-header grading-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="grading-modal-icon info">
                  <i className="ri-pencil-line"></i>
                </div>

                <div>
                  <h5 className="modal-title mb-1">Update Grade</h5>

                  <small className="text-muted">
                    Modify grade percentage configuration
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                id="updateGradingClose"
              ></button>
            </div>

            <div className="modal-body">
              <input type="hidden" value={updateData.id} />

              <div className="grading-form-section">
                <div className="grading-form-section-title">
                  <i className="ri-award-line"></i>
                  Grade Details
                </div>

                <div className="form-floating form-floating-outline">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Grade"
                    value={updateData.grade}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        grade: e.target.value.toUpperCase(),
                      })
                    }
                  />

                  <label>Grade</label>
                </div>
              </div>

              <div className="grading-form-section mt-4">
                <div className="grading-form-section-title">
                  <i className="ri-percent-line"></i>
                  Percentage Range
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="form-control"
                        placeholder="From Percentage"
                        value={updateData.from_percentage}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            from_percentage: e.target.value,
                          })
                        }
                      />

                      <label>From (%)</label>
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="form-control"
                        placeholder="To Percentage"
                        value={updateData.to_percentage}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            to_percentage: e.target.value,
                          })
                        }
                      />

                      <label>To (%)</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grading-form-section mt-4">
                <div className="grading-form-section-title">
                  <i className="ri-toggle-line"></i>
                  Availability
                </div>

                <div className="form-floating form-floating-outline">
                  <select
                    className="form-select"
                    value={updateData.status}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                  </select>

                  <label>Status</label>
                </div>
              </div>
            </div>

            <div className="modal-footer grading-modal-footer">
              <button
                type="button"
                className="btn btn-label-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                <i className="ri-save-line me-1"></i>
                Update Grade
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* =================================================
                STATUS UPDATE MODAL
            ================================================= */}
      <div
        className="modal fade"
        id="statusGradingModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <form
            className="modal-content grading-modal"
            onSubmit={handleStatusUpdate}
          >
            <div className="modal-header grading-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="grading-modal-icon warning">
                  <i className="ri-toggle-line"></i>
                </div>

                <div>
                  <h5 className="modal-title mb-1">Grade Status</h5>

                  <small className="text-muted">Change availability</small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                id="statusGradingClose"
              ></button>
            </div>

            <div className="modal-body">
              <div className="status-grade-preview">
                <span>Selected Grade</span>

                <strong>{statusData.grade || "-"}</strong>
              </div>

              <div className="form-floating form-floating-outline mt-3">
                <select
                  className="form-select"
                  value={statusData.status}
                  onChange={(e) =>
                    setStatusData({
                      ...statusData,
                      status: e.target.value,
                    })
                  }
                >
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </select>

                <label>Status</label>
              </div>
            </div>

            <div className="modal-footer grading-modal-footer">
              <button
                type="button"
                className="btn btn-label-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                <i className="ri-check-line me-1"></i>
                Update
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* =================================================
                UI STYLES
            ================================================= */}
      <style>{`
                .grading-page-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    padding: 16px 18px;
                    border: 1px solid var(--bs-border-color);
                    border-radius: 12px;
                    background: transparent;
                }

                .grading-header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .grading-header-icon {
                    width: 42px;
                    height: 42px;
                    min-width: 42px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    background: rgba(var(--bs-primary-rgb), .10);
                    color: var(--bs-primary);
                    font-size: 21px;
                }

                .grading-page-header h4 {
                    font-size: 17px;
                    font-weight: 700;
                }

                .grading-page-header p {
                    font-size: 11px;
                    color: var(--bs-secondary-color);
                }

                .grading-add-btn {
                    border-radius: 8px;
                    padding: 8px 14px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .grading-filter-card,
                .grading-list-card {
                    border: 1px solid var(--bs-border-color);
                    border-radius: 12px;
                    overflow: hidden;
                }

                .grading-filter-card .card-body {
                    padding: 15px 17px;
                }

                .grading-filter-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .grading-filter-icon {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    background: rgba(var(--bs-primary-rgb), .08);
                    color: var(--bs-primary);
                    font-size: 16px;
                }

                .grading-filter-header h6 {
                    font-size: 13px;
                    font-weight: 600;
                }

                .grading-filter-header small {
                    font-size: 10px;
                }

                .grading-search-box {
    position: relative;
    width: 100%;
}

                .grading-search-box > i {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    color: var(--bs-secondary-color);
    font-size: 17px;
    pointer-events: none;
}

                .grading-search-box .form-control {
    width: 100%;
    height: 39px;
    padding-left: 42px !important;
    padding-right: 40px !important;
    font-size: 12px;
    border-radius: 8px;
    position: relative;
    z-index: 1;
}

.grading-search-box .form-control:focus {
    padding-left: 42px !important;
    padding-right: 40px !important;
}

                .grading-search-clear {
    position: absolute;
    right: 7px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
    width: 27px;
    height: 27px;
    border: 0;
    background: transparent;
    color: var(--bs-secondary-color);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

                .grading-search-clear:hover {
    background: rgba(127, 127, 127, 0.08);
    color: var(--bs-danger);
}

                .grading-filter-btn {
                    height: 39px;
                    border-radius: 8px;
                    font-size: 11px;
                    font-weight: 600;
                }

                .grading-list-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 17px;
                    background: transparent;
                    border-bottom: 1px solid var(--bs-border-color);
                }

                .grading-list-header h5 {
                    font-size: 14px;
                    font-weight: 700;
                }

                .grading-list-header small {
                    display: block;
                    margin-top: 3px;
                    font-size: 10px;
                }

                .grading-table {
                    min-width: 900px;
                }

                .grading-table thead th {
                    padding: 10px 12px;
                    background: rgba(127, 127, 127, .045);
                    border-bottom: 1px solid var(--bs-border-color);
                    color: var(--bs-secondary-color);
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    white-space: nowrap;
                }

                .grading-table tbody td {
                    padding: 11px 12px;
                    border-bottom: 1px solid var(--bs-border-color);
                    font-size: 12px;
                    vertical-align: middle;
                }

                .grading-table tbody tr:last-child td {
                    border-bottom: 0;
                }

                .grading-table tbody tr {
                    transition: background .15s ease;
                }

                .grading-table tbody tr:hover {
                    background: rgba(127, 127, 127, .035);
                }

                .grading-number-col {
                    width: 55px;
                }

                .grading-action-col {
                    width: 105px;
                }

                .grading-row-number {
                    width: 27px;
                    height: 27px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 7px;
                    background: rgba(127, 127, 127, .08);
                    color: var(--bs-secondary-color);
                    font-size: 10px;
                    font-weight: 600;
                }

                .grade-cell {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .grade-avatar {
                    width: 34px;
                    height: 34px;
                    min-width: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 9px;
                    background: rgba(var(--bs-primary-rgb), .10);
                    color: var(--bs-primary);
                    font-size: 13px;
                    font-weight: 700;
                }

                .grade-name {
                    font-size: 13px;
                    font-weight: 700;
                }

                .grade-cell small {
                    font-size: 9px;
                }

                .percentage-value {
                    font-weight: 600;
                    white-space: nowrap;
                }

                .range-cell {
                    width: 150px;
                }

                .range-label {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 5px;
                    color: var(--bs-secondary-color);
                    font-size: 9px;
                    font-weight: 600;
                }

                .grading-range-bar {
                    height: 5px;
                    width: 100%;
                    overflow: hidden;
                    border-radius: 10px;
                    background: rgba(127, 127, 127, .12);
                }

                .grading-range-fill {
                    height: 100%;
                    min-width: 8px;
                    border-radius: inherit;
                    background: var(--bs-primary);
                }

                .grading-status-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    border: 0;
                    padding: 5px 8px;
                    border-radius: 7px;
                    font-size: 10px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all .15s ease;
                }

                .grading-status-btn.active {
                    background: rgba(var(--bs-success-rgb), .10);
                    color: var(--bs-success);
                }

                .grading-status-btn.inactive {
                    background: rgba(var(--bs-danger-rgb), .10);
                    color: var(--bs-danger);
                }

                .grading-status-btn:hover {
                    filter: brightness(.95);
                }

                .status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: currentColor;
                }

                .grading-status-btn i {
                    font-size: 13px;
                    opacity: .7;
                }

                .grading-actions {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                }

                .grading-action-btn {
                    width: 31px;
                    height: 31px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid var(--bs-border-color);
                    border-radius: 7px;
                    background: transparent;
                    font-size: 15px;
                    transition: all .15s ease;
                }

                .grading-action-btn.edit {
                    color: var(--bs-info);
                }

                .grading-action-btn.remove {
                    color: var(--bs-danger);
                }

                .grading-action-btn:hover {
                    color: #fff;
                }

                .grading-action-btn.edit:hover {
                    background: var(--bs-info);
                    border-color: var(--bs-info);
                }

                .grading-action-btn.remove:hover {
                    background: var(--bs-danger);
                    border-color: var(--bs-danger);
                }

                .grading-empty-state {
                    padding: 55px 20px;
                    text-align: center;
                }

                .grading-empty-icon {
                    width: 52px;
                    height: 52px;
                    margin: 0 auto 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    background: rgba(127, 127, 127, .08);
                    color: var(--bs-secondary-color);
                    font-size: 24px;
                }

                .grading-empty-state h6 {
                    margin-bottom: 4px;
                    font-size: 13px;
                    font-weight: 600;
                }

                .grading-empty-state p {
                    margin-bottom: 12px;
                    color: var(--bs-secondary-color);
                    font-size: 11px;
                }

                /* =================================================
                   MODALS
                ================================================= */

                .grading-modal {
                    border: 1px solid var(--bs-border-color);
                    border-radius: 13px;
                    overflow: hidden;
                }

                .grading-modal-header {
                    padding: 15px 17px;
                    border-bottom: 1px solid var(--bs-border-color);
                }

                .grading-modal-header .modal-title {
                    font-size: 15px;
                    font-weight: 700;
                }

                .grading-modal-header small {
                    font-size: 10px;
                }

                .grading-modal-icon {
                    width: 38px;
                    height: 38px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 9px;
                    font-size: 18px;
                }

                .grading-modal-icon.primary {
                    background: rgba(var(--bs-primary-rgb), .10);
                    color: var(--bs-primary);
                }

                .grading-modal-icon.info {
                    background: rgba(var(--bs-info-rgb), .10);
                    color: var(--bs-info);
                }

                .grading-modal-icon.warning {
                    background: rgba(var(--bs-warning-rgb), .12);
                    color: var(--bs-warning);
                }

                .grading-modal .modal-body {
                    padding: 18px;
                }

                .grading-form-section-title {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    margin-bottom: 10px;
                    color: var(--bs-secondary-color);
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                }

                .grading-form-section-title i {
                    color: var(--bs-primary);
                    font-size: 14px;
                }

                .grading-modal .form-control,
                .grading-modal .form-select {
                    font-size: 12px;
                }

                .grading-example-box {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 12px;
                    border: 1px solid rgba(var(--bs-info-rgb), .15);
                    border-radius: 8px;
                    background: rgba(var(--bs-info-rgb), .06);
                }

                .grading-example-icon {
                    width: 28px;
                    height: 28px;
                    min-width: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 7px;
                    background: rgba(var(--bs-info-rgb), .10);
                    color: var(--bs-info);
                }

                .grading-example-box strong {
                    display: block;
                    font-size: 10px;
                    margin-bottom: 1px;
                }

                .grading-example-box span {
                    display: block;
                    color: var(--bs-secondary-color);
                    font-size: 10px;
                }

                .status-grade-preview {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 13px;
                    border: 1px solid var(--bs-border-color);
                    border-radius: 9px;
                    background: rgba(127, 127, 127, .045);
                }

                .status-grade-preview span {
                    color: var(--bs-secondary-color);
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: .3px;
                }

                .status-grade-preview strong {
                    font-size: 17px;
                    color: var(--bs-primary);
                }

                .grading-modal-footer {
                    padding: 12px 17px;
                    border-top: 1px solid var(--bs-border-color);
                }

                .grading-modal-footer .btn {
                    font-size: 11px;
                    border-radius: 7px;
                }

                @media (max-width: 767.98px) {
                    .grading-page-header {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .grading-add-btn {
                        width: 100%;
                    }

                    .grading-filter-header {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .grading-search-box {
                        width: 100%;
                    }

                    .grading-list-header {
                        align-items: flex-start;
                        flex-direction: column;
                        gap: 8px;
                    }

                    .range-cell {
                        width: 130px;
                    }
                }
            `}</style>
    </>
  );
}
